import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Printer, ArrowLeft, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getArrivalNoticeList } from "@/redux/ImportBooking/actions";
import { getAddressAPI } from "@/redux/Purchase/actions";
import { getFileManagerList, getMasterCompanyDetails } from "@/redux/Common/actions";
import { loaderStart, loaderStop } from "@/redux/Loader/actions";

const BookingImportPrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        MasterCompanyDetails,
        fileManagerList,
    } = useSelector((state) => state.common);

    const { id: encodedData } = useParams();
    const bookingid = atob(encodedData); // Decode the base64 encoded ID

    const [formData, setFormData] = useState(null);
    const [chargesByContainer, setChargesByContainer] = useState([]);
    const [containerTypes, setContainerTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const printRef = useRef();
    const [customerAddress, setCustomerAddress] = useState();
    const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
    const companyId = authUser?.companyId;
    const [arCharges, setArCharges] = useState([]);
    const [apCharges, setApCharges] = useState([]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                if (bookingid && bookingid !== 0) {
                    loadCommonData();
                    // console.log("Fetching existing booking data for bookingid:", bookingid);
                }
            } catch (error) {
                // console.error("Error initializing booking:", error);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);


    const loadCommonData = async () => {
        dispatch(getMasterCompanyDetails(authUser?.companyId));
        _getBooking();
        dispatch(getFileManagerList());
    };

    const _getBooking = async () => {

        try {
            let response;

            if (
                typeof bookingid === "number" ||
                (typeof bookingid === "string" && bookingid !== "0")
            ) {
                response = await dispatch(
                    getArrivalNoticeList({
                        BolID: bookingid,
                        CompanyID: companyId,
                    }),
                );

                // console.log("aaaaaaaaaaaaaa:", response);

                if (response) {
                    _setAddress("customer", response?.customerId);

                    setFormData(response);

                } else {
                    // console.warn("Unexpected API response format:", response);
                }
            } else if (bookingid && typeof bookingid === "object") {
                setFormData(bookingid);

            }
        } catch (error) {
            // console.error("Error fetching booking:", error);
        }
    };

    const _setAddress = async (type, id) => {
        const address = await getAddressAPI(id);

        setCustomerAddress(address);
    };


    const handlePrint = () => {
        const printContent = printRef.current.innerHTML;

        // Create hidden iframe
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";

        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;

        iframeDoc.open();
        iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Booking - ${formData?.fileNumber || "Booking"}</title>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
                @page {
                    size: A4;
                    margin: 15mm;
                }

                body {
                    margin: 0;
                    padding: 0;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            </style>
        </head>
        <body class="bg-white">
            ${printContent}
        </body>
        </html>
    `);
        iframeDoc.close();

        // Print after content loads
        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            // Cleanup after print
            iframe.contentWindow.onafterprint = () => {
                document.body.removeChild(iframe);
            };
        };
    };

    const handleDownload = async () => {
        if (!printRef.current || !formData) return;

        try {
            const downloadBtn = document.querySelector("[data-download-btn]");
            const originalText = downloadBtn?.textContent;

            if (downloadBtn) {
                downloadBtn.textContent = "Generating PDF...";
                downloadBtn.disabled = true;
            }

            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = imgWidth / imgHeight;

            let width = pageWidth - 20;
            let height = width / ratio;

            if (height > pageHeight - 20) {
                height = pageHeight - 20;
                width = height * ratio;
            }

            const x = (pageWidth - width) / 2;
            const y = (pageHeight - height) / 2;

            pdf.addImage(imgData, "PNG", x, y, width, height);
            pdf.save(
                `Booking_${formData.fileNumber || "Document"}_${moment().format("YYYY-MM-DD")}.pdf`,
            );

            if (downloadBtn) {
                downloadBtn.textContent = originalText;
                downloadBtn.disabled = false;
            }
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try printing instead.");
            const downloadBtn = document.querySelector("[data-download-btn]");
            if (downloadBtn) {
                downloadBtn.textContent = "Download PDF";
                downloadBtn.disabled = false;
            }
        }
    };

    const calculateTotal = (containerType = null) => {
        if (!formData?.rates || !Array.isArray(formData.rates)) return 0;

        if (containerType) {
            return formData.rates
                .filter((rate) => rate.contType === containerType)
                .reduce((sum, rate) => sum + (parseFloat(rate.sellAmount) || 0), 0);
        }
        return formData.rates.reduce(
            (sum, rate) => sum + (parseFloat(rate.sellAmount) || 0),
            0,
        );
    };

    const formatDate = (dateString, y) => {
        if (!dateString) return "";
        console.log(
            "dateString",
            dateString,
            moment(dateString).format("DD MMM YYYY"),
            y,
        );
        return moment(dateString).isValid()
            ? moment(dateString).format("DD MMM YYYY")
            : "";
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        return moment(dateString).isValid()
            ? moment(dateString).format("DD MMM YYYY HH:mm")
            : "";
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading booking data...</p>
                </div>
            </div>
        );
    }

    if (!formData) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading booking data...</p>
                </div>
            </div>
        );
    }



    const totals = formData?.BOLDescriptionsData?.reduce(
        (acc, item) => {
            acc.containers += item?.ContainerNumber ? 1 : 0;
            acc.pcs += Number(item?.NoPackage || 0);
            acc.kgs += Number(item?.NetKgs || 0);
            acc.cbm += Number(item?.CBM || 0);
            return acc;
        },
        { containers: 0, pcs: 0, kgs: 0, cbm: 0 }
    );

    const fileManager = fileManagerList.find(item => item.id === formData?.FileManager);

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white">
            {/* Action Bar */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center">
                    {/* <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Booking
                    </button> */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleDownload}
                            data-download-btn
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300"
                        >
                            <Download className="w-4 h-4" />
                            Download PDF
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>

            {/* A4 Print Container */}
            <div className="flex justify-center py-6 print:py-0">
                <div
                    ref={printRef}
                    className="a4-page bg-white shadow-xl print:shadow-none"
                >
                    {/* Outer table: thead repeats on each printed page */}
                    <table className="print-root-table w-full border-collapse">
                        <thead>
                            <tr>
                                <td className="p-0 border-0 align-top print-doc-header-cell">
                                    {/* Logo header — flow layout so repeated thead height is correct (no overlap on page 2+) */}
                                    <div className="print-doc-header flex items-start gap-3 px-4 pt-4 pb-3">
                                        <div className="flex flex-shrink-0 items-center gap-3">
                                            <div>
                                                <img
                                                    src={MasterCompanyDetails?.[0]?.CompanyLogoDark}
                                                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                                                    className="h-12 w-auto"
                                                />
                                            </div>
                                        </div>

                                        <div className="min-w-0 flex-1 flex justify-center pt-1">
                                            <h1 className="text-xl font-extrabold text-black tracking-wide whitespace-nowrap text-center">
                                                ARRIVAL NOTICE
                                            </h1>
                                        </div>

                                        <div className="flex-shrink-0 text-right text-[10px] text-black font-bold max-w-[200px] break-words">
                                            {MasterCompanyDetails?.[0]?.ProfileAddressPrint?.split(",").map(
                                                (line, index) => (
                                                    <p key={index}>{line.trim()}</p>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-0 border-0 align-top print-doc-body-cell">
                                    {/* Content */}
                                    <div className="page">
                                        <div className="text-[10px]">
                                            <table className="arrival-notice-block w-full border-collapse border border-black">
                                                <colgroup>
                                                    <col style={{ width: "58%" }} />
                                                    <col style={{ width: "21%" }} />
                                                    <col style={{ width: "21%" }} />
                                                </colgroup>
                                                <tbody>
                                                    <tr>
                                                        <td rowSpan={2} className="border-r border-b border-black p-2">
                                                            <p className="font-bold">SHIPPER :</p>
                                                            <p className="whitespace-pre-line">{formData?.Shipper + "\n" + formData?.ShipperAddress}</p>
                                                        </td>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">REFERENCE NO :</span> {formData?.FileRefNumber}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">PRINTED DATE :</span>{" "}
                                                            {moment().format("DD MMMM YYYY").toUpperCase()}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">MBL # :</span> {formData?.CarrierMBLNo + " " + formData?.MBL_SCACCode}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">HBL # :</span> {formData?.HBLNumber}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td rowSpan={2} className="border-r border-b border-black p-2">
                                                            <p className="font-bold">CONSIGNEE :</p>
                                                            <p className="whitespace-pre-line">{formData?.Consignee + "\n" + formData?.ConsigneeAddress}</p>
                                                        </td>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">AMS HBL # :</span> {formData?.HBLNumber}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">PREPARED BY :</span> {fileManager?.filemanager}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">VSL / VOY :</span> {formData?.VesselName}/{formData?.Voyage}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">MOVE TYPE :</span> {formData?.MoveType}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td rowSpan={3} className="border-r border-b border-black p-2">
                                                            <span className="font-bold whitespace-pre-line">NOTIFY :</span> {formData?.Notify + "\n" + formData?.NotifyAddress}
                                                        </td>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">PLACE OF RECEIPT :</span> {formData?.PalceOfReceipt}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">E.T.D :</span>{" "}
                                                            {formData?.EtdAtPol
                                                                ? moment(formData.EtdAtPol).format("DD MMM YYYY").toUpperCase()
                                                                : ""}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">PORT OF LOAD :</span> {formData?.PortOfLoading}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">E.T.D :</span>{" "}
                                                            {formData?.ETAPOD
                                                                ? moment(formData.ETAPOD).format("DD MMM YYYY").toUpperCase()
                                                                : ""}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">TRANSSHIPMENT PORT :</span> {formData?.PortofTransshipment}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">E.T.D :</span>{" "}
                                                            {formData?.ETDAtTransshipment
                                                                ? moment(formData.ETDAtTransshipment).format("DD MMM YYYY").toUpperCase()
                                                                : ""}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td rowSpan={2} className="border-r border-b border-black p-2">
                                                            <span className="font-bold whitespace-pre-line">CARGO PICKUP LOCATION :</span> {formData?.FreightLocationAddress}{"\n"}
                                                            <br /> firms code : {formData?.FirmsCode}
                                                        </td>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">PORT OF DISCHARGE :</span> {formData?.PortOfDischarge}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">E.T.A :</span>{" "}
                                                            {formData?.ETAPOD
                                                                ? moment(formData.ETAPOD).format("DD MMM YYYY").toUpperCase()
                                                                : ""}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border-r border-b border-black p-2">
                                                            <span className="font-bold">PLACE OF DELIVERY :</span> {formData?.PlaceOfDelivery}
                                                        </td>
                                                        <td className="border-b border-black p-2">
                                                            <span className="font-bold">E.T.A :</span>{" "}
                                                            {formData?.ETADestination
                                                                ? moment(formData.ETADestination).format("DD MMM YYYY").toUpperCase()
                                                                : ""}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td rowSpan={2} className="border-r border-b border-black p-2">
                                                            <p className="font-bold">CARGO DESCRIPTIONS :</p>
                                                            {(formData?.CargoDescription || "").trim() ? (
                                                                <p className="whitespace-pre-wrap">{formData.CargoDescription}</p>
                                                            ) : null}
                                                        </td>
                                                        <td colSpan={2} className="arrival-td-flush border-b border-black">
                                                            <div className="arrival-it-strip grid grid-cols-3 border-b border-black text-[11px]">
                                                                <div className="border-r border-black">
                                                                    <span className="font-bold">IT NO :</span> {formData?.ITNumber}
                                                                </div>
                                                                <div className="border-r border-black">
                                                                    <span className="font-bold">IT DATE :</span>{" "}
                                                                    {formData?.ITDate
                                                                        ? moment(formData.ITDate).format("DD MMM YYYY").toUpperCase()
                                                                        : ""}
                                                                </div>
                                                                <div>
                                                                    <span className="font-bold">IT PORT :</span> {formData?.ITPlace}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="border-b border-black">
                                                            <p className="text-red-600 font-bold leading-tight m-0">PAYMENT MODE: COLLECT</p>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* ─── TABLE — single-line headers fix ─── */}
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>CONTAINER NO.</th>
                                                    <th>SIZE TYPE</th>
                                                    <th>PCS</th>
                                                    <th>WT. KGS</th>
                                                    <th>CBM</th>
                                                    <th>SEAL NO.</th>
                                                    <th>IT NO.</th>
                                                    <th>CN/CPRS NO.</th>
                                                    <th>PICKUP NO.</th>
                                                    <th>LAST FREE DAY</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {formData?.BOLDescriptionsData?.length ? (
                                                    formData.BOLDescriptionsData.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item?.ContainerNumber || "-"}</td>
                                                            <td>{item?.ContTypeName || "-"}</td>
                                                            <td>{item?.NoPackage || "-"}</td>
                                                            <td>{item?.NetKgs || "-"}</td>
                                                            <td>{item?.CBM || "-"}</td>
                                                            <td>{item?.SealNumber || "-"}</td>
                                                            <td>{item?.ITNumber || "-"}</td>
                                                            <td>{item?.CNCPRSNumber || "-"}</td>
                                                            <td>{item?.PickUpNumber || "-"}</td>
                                                            <td>
                                                                {item?.LastDate
                                                                    ? moment(item.LastDate).format("DD MMM YYYY").toUpperCase()
                                                                    : "-"}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center font-bold">
                                                            No Data Available
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                        {/* Footer */}
                                        <div className="footer text-black font-bold">
                                            <p>
                                                TOTAL CONTAINERS: {totals?.containers || 0}{" "}
                                                PCS: {totals?.pcs || 0}{" "}
                                                KGS: {totals?.kgs?.toFixed(3) || "0.000"}{" "}
                                                CBM: {totals?.cbm?.toFixed(3) || "0.000"}
                                            </p>

                                            <p className="remarks mt-1">
                                                DELIVERY REMARKS:
                                                <div className="p-1 border border-black h-12 mt-1">
                                                    {formData?.DeliveryRemarks}
                                                </div>
                                            </p>
                                        </div>

                                        <div className="border-t border-black mt-2 text-[11px]">
                                            <div className="grid grid-cols-2">
                                                {/* LEFT SECTION */}
                                                <div className="border-r border-black">
                                                    <div className="font-bold border-b border-black p-1.5">
                                                        TO ALL CUSTOMERS
                                                    </div>
                                                    <div className="p-1.5 space-y-0.5">
                                                        <p>
                                                            <span className="font-bold">1.</span> PLEASE SEND MONEY
                                                            ORDER OR CASHIER'S CHECK MAKE PAYABLE TO
                                                        </p>
                                                        <p>
                                                            {MasterCompanyDetails?.[0]?.CompanyName} — PLEASE REMIT PAYMENT
                                                        </p>
                                                        <p>
                                                            <span className="font-bold">2.</span> WE CAN ONLY RELEASE
                                                            THE FREIGHT UPON RECEIPT OF CHARGES SHOWN BELOW AND
                                                            PROPERLY ENDORSED ORIGINAL HOUSE BILL OF LADING.
                                                        </p>
                                                        <p>
                                                            <span className="font-bold">3.</span> PRIOR TO CARGO
                                                            PICK-UP, CONSIGNEE MUST CLEAR CUSTOMS AND CONFIRM FREIGHT
                                                            RELEASE. PLEASE PICK-UP YOUR CARGO ASAP TO AVOID CHARGES.
                                                        </p>
                                                        <p>
                                                            <span className="font-bold">4.</span> THESE CHARGES ARE
                                                            FOR THE ACCOUNT OF ULTIMATE CONSIGNEE TO WHOM THIS FREIGHT
                                                            IS RELEASED.
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* RIGHT SECTION - CHARGES */}
                                                <div className="flex flex-col h-full border border-black">
                                                    <div className="grid grid-cols-3 font-bold border-b border-black">
                                                        <div className="p-2 text-center border-r border-black">
                                                            DESCRIPTION OF CHARGES
                                                        </div>
                                                        <div className="p-2 text-center border-r border-black">
                                                            PREPAID
                                                        </div>
                                                        <div className="p-2 text-center">
                                                            COLLECT
                                                        </div>
                                                    </div>

                                                    <div className="flex-grow border-b border-black relative min-h-[56px]">
                                                        <div className="absolute top-0 bottom-0 left-1/3 border-l border-black"></div>
                                                        <div className="absolute top-0 bottom-0 left-2/3 border-l border-black"></div>
                                                    </div>

                                                    <div className="grid grid-cols-3 font-bold border-b border-black min-h-[24px]">
                                                        <div className="p-1 border-r border-black">
                                                            TOTAL AMOUNT
                                                        </div>
                                                        <div className="p-1 border-r border-black text-right">

                                                        </div>
                                                        <div className="p-1 text-right">

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border-t border-black mt-2 text-[11px]"></div>

                                            <div className="p-1 text-left text-[10px] text-gray-800 font-bold mt-2">
                                                Powered by {MasterCompanyDetails?.[0]?.CompanyName}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* ─── STYLES ─── */}
                    <style>{`

.print-root-table {
  width: 100%;
  border-collapse: collapse;
}

.print-root-table > tbody > tr > td {
  padding: 0 !important;
  border: none !important;
  vertical-align: top;
}

.print-root-table thead td.print-doc-header-cell {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 4px !important;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid #000 !important;
  vertical-align: top;
}

.print-doc-body-cell .page {
  padding-top: 6px;
}

.arrival-notice-block {
  table-layout: fixed;
}
.arrival-notice-block td {
  vertical-align: top;
  box-sizing: border-box;
  padding: 2px 6px !important;
  line-height: 1.28;
}
.arrival-notice-block td p {
  margin: 0;
}
.arrival-notice-block td p + p {
  margin-top: 2px;
}
.arrival-notice-block .arrival-it-strip > div {
  padding: 2px 6px !important;
  box-sizing: border-box;
}
.arrival-notice-block td.arrival-td-flush {
  padding: 0 !important;
}

@media print {
  .print-root-table thead {
    display: table-header-group;
  }
  .print-root-table tbody {
    display: table-row-group;
  }
  .print-root-table thead tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .print-doc-header {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .print-root-table thead td.print-doc-header-cell {
    padding-bottom: 8px !important;
  }
  .print-doc-body-cell .page {
    padding-top: 10px;
  }
  .table thead {
    display: table-header-group;
  }
  .table tbody {
    display: table-row-group;
  }
}
 
.page {
  width: min(748px, 100%);
  max-width: 748px;
  margin: auto;
  padding: 0 4px;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
  background: #fff;
  color: #000;
}
 
/* ── TABLE: single-line headers ── */
.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  table-layout: auto;          /* let columns breathe equally */
}
 
/* HEADER CELLS — the key fix */
.table thead tr {
  background-color: #f0f0f0;  /* subtle grey so headers pop */
}
 
.table th {
  border: 1px solid #000;
  padding: 3px 2px;            /* tighter vertical + horizontal padding */
  font-size: 8.5px;            /* slightly smaller than body → fits one line */
  font-weight: 700;
  text-align: center;
  white-space: nowrap;         /* ← prevents wrapping */
  line-height: 1.2;
  letter-spacing: 0.2px;
}
 
/* BODY CELLS */
.table td {
  border: 1px solid #000;
  padding: 3px 2px;
  font-size: 9px;
  text-align: center;
}
 
/* ── REST OF STYLES (unchanged) ── */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
}
 
.logo { font-weight: bold; }
 
.title {
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #000;
  display: block;
  width: 100%;
}
 
.company {
  text-align: right;
  font-size: 12px;
}
 
.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid #000;
  page-break-inside: avoid;
}
 
.grid-3 > div {
  padding: 10px;
  border-right: 1px solid #000;
  break-inside: avoid;
}
 
.grid-3 > div:last-child { border-right: none; }
 
.bordered { border-top: 1px solid #000; }
.label { font-weight: bold; font-size: 12px; }
.mt { margin-top: 10px; }
 
.cargo {
  display: grid;
  grid-template-columns: 2fr 1fr;
  border: 1px solid #000;
}
 
.cargo .left,
.cargo .right { padding: 10px; }
 
.cargo .right { border-left: 1px solid #000; }
 
.payment { color: red; font-weight: bold; margin-top: 10px; }
 
.footer { margin-top: 8px; font-size: 10px; }
.remarks { margin-top: 10px; }
 
        `}</style>
                </div>

                {/* Print Styles */}
                <style>{`
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
 
        @media screen {
          .a4-page { margin: 0 auto; }
        }
 
        @media print {
          @page { size: A4; margin: 6mm; }
 
          html, body {
            width: 210mm;
            height: 297mm;
          }
 
          body {
            margin: 0;
            padding: 0;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
 
          .a4-page {
            width: 100%;
            min-height: auto;
            padding: 0;
            margin: 0;
            box-shadow: none !important;
          }

          .page {
            width: 100%;
          }
 
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:bg-white { background: white !important; }

          .print-root-table thead {
            display: table-header-group;
          }
          .print-root-table tbody {
            display: table-row-group;
          }
          .table thead {
            display: table-header-group;
          }
          .table tbody {
            display: table-row-group;
          }

          .print-root-table thead tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .print-doc-header {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .print-root-table thead td.print-doc-header-cell {
            padding-bottom: 8px !important;
          }
          .print-doc-body-cell .page {
            padding-top: 10px;
          }
        }
      `}</style>
            </div>
        </div>
    );
};

export default BookingImportPrint;
